import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom"

import moment from "moment/moment"
import Filter from "pages/orders/filter"
import nProgress from "nprogress"
import { useAuth } from "contexts/auth-context"
import { Pagination } from "components"
import { lang } from "config"
import { Pencil, Plus } from "components/icons/outline"
import { usePage } from "contexts/page-context"
import useOrderRepositories from "repositories/order"
import Table from "components/table"
import useBranchRepositories from "repositories/branch"
import axios from "axios"
import useDebouncedSearchParam from "hooks/use-debounced-search-param"

export default function List() {
    // Logged in user ability
    const { can, isSuper, currentBranch } = useAuth()
    // Configuration
    const { configuration } = usePage()

    // Transition
    const { title, transition, setTransition } = useOutletContext()

    // React router navigate hook
    const navigate = useNavigate()

    // React router location hook
    const location = useLocation()

    // Query parameters
    const [searchParams, setSearchParams] = useSearchParams()

    const searchInput = useRef()

    const page = searchParams.get('page')
    const search = searchParams.get('search')
    const [searchTemp, setSearchTemp] = useDebouncedSearchParam({ search, searchParams, setSearchParams })
    const productUnit = searchParams.get('product_unit_id')
    const branch = currentBranch ? currentBranch.id : searchParams.get('branch_id')
    const from = searchParams.get('from')
    const to = searchParams.get('to')
    const status = searchParams.get('status')

    const orderBy = searchParams.get('order_by')
    const direction = searchParams.get('direction')

    const { data: data, isLoading: isLoadingdata, mutate: mutateData } = useOrderRepositories({
        page,
        search,
        outlet_id: branch,
        start_date:from,
        end_date:to,
        status,
        order_by: orderBy,
        direction
    })

    const { data: branchData, isLoading: isLoadingBranchSelection } = useBranchRepositories({
        paginate: false
    })

    // -------------------- modal detail state --------------------
    const [isDetailOpen, setIsDetailOpen] = useState(false)
    const [selectedId, setSelectedId] = useState(null)
    const [detail, setDetail] = useState(null)
    const [loadingDetail, setLoadingDetail] = useState(false)
    const [actionLoading, setActionLoading] = useState(false)
    const [forceReadyPickup, setForceReadyPickup] = useState(false)

    const toNumber = (v) => {
    if (v == null) return 0
    const n = Number(String(v).replace(/,/g, ''))
    return Number.isFinite(n) ? n : 0
    }
    const money = (v) => currency(toNumber(v))

    const formatUang = (value, opt = {}) => {
        const n = Number(value ?? 0);
        if (!Number.isFinite(n)) return "0";

        return new Intl.NumberFormat("id-ID", {
            style: "decimal",            // ✅ tanpa currency
            useGrouping: true,           // ✅ ribuan
            minimumFractionDigits: opt.minFrac ?? 0,
            maximumFractionDigits: opt.maxFrac ?? 0,
        }).format(n);
    };

    const detailStatus = useMemo(() => {
    const s = String(detail?.payment_status ?? '').toUpperCase()
    return s
    }, [detail])

    const apiGetOrderDetail = async (id) => {
    // TODO: ganti ke repo/axios internal kamu
    return axios.get(`${process.env.REACT_APP_INVENTORY_API_URL}/orders/${id}`).then(r => r.data)
    }

    const apiCancel = async ({ orderId, reason,mode }) => {
    // TODO: ganti ke repo/axios internal kamu
    return axios.post(`${process.env.REACT_APP_INVENTORY_API_URL}/orders/${orderId}/cancel`, { order_id: orderId, reason,mode })
    }

    const apiApprovePayment = async ({ orderId }) => {
    // TODO: ganti ke repo/axios internal kamu
    return axios.post(`${process.env.REACT_APP_INVENTORY_API_URL}/orders/${orderId}/approve-payment`)
    }

    const apiReadyPickup = async ({ orderId, note, force }) => {
    // TODO: ganti ke repo/axios internal kamu
    return axios.post(`${process.env.REACT_APP_YOIBOI_API_URL}/api/v1/orders/${orderId}/ready-pickup`, { note, force })
    }

    const openDetail = useCallback(async (id) => {
        setIsDetailOpen(true)
        setSelectedId(id)
        setDetail(null)
        setForceReadyPickup(false)

        setLoadingDetail(true)
        try {
            const res = await apiGetOrderDetail(id)
            setDetail(res)
        } catch (e) {
            console.error(e)
        } finally {
            setLoadingDetail(false)
        }
    }, [])

    const closeDetail = useCallback(() => {
    setIsDetailOpen(false)
    setSelectedId(null)
    setDetail(null)
    setForceReadyPickup(false)
    setActionLoading(false)
    }, [])

    const onCancel = useCallback(async () => {
    if (!detail?.id) return
    const ok = window.confirm('Reject pembayaran untuk order ini?')
    if (!ok) return

    setActionLoading(true)
    try {
        await apiCancel({
        orderId: detail.id,
        reason: 'Cancel from CMS',
        mode:'cancel'
        })
        await mutateData()
        closeDetail()
    } catch (e) {
        console.error(e)
        alert('Gagal Cancel.')
    } finally {
        setActionLoading(false)
    }
    }, [detail, mutateData, closeDetail])

    const onVoid = useCallback(async () => {
    if (!detail?.id) return
    const ok = window.confirm('Reject pembayaran untuk order ini?')
    if (!ok) return

    setActionLoading(true)
    try {
        await apiCancel({
        orderId: detail.id,
        reason: 'Cancel from CMS',
        mode:'void'
        })
        await mutateData()
        closeDetail()
    } catch (e) {
        console.error(e)
        alert('Gagal void payment.')
    } finally {
        setActionLoading(false)
    }
    }, [detail, mutateData, closeDetail])

    const onApprove = useCallback(async () => {
    if (!detail?.id) return
    const ok = window.confirm('Approve pembayaran untuk order ini?')
    if (!ok) return

    setActionLoading(true)
    try {
        await apiApprovePayment({
            orderId: detail.id,
        })
        await mutateData()
        closeDetail()
    } catch (e) {
        console.error(e)
        alert('Gagal approve payment.')
    } finally {
        setActionLoading(false)
    }
    }, [detail, mutateData, closeDetail])

    const onReadyPickup = useCallback(async () => {
    if (!detail?.id) return
    const ok = window.confirm('Set order ini menjadi Ready To Pickup?')
    if (!ok) return

    setActionLoading(true)
    try {
        await apiReadyPickup({
        orderId: detail.id,
        note: 'Set dari CMS',
        force: !!forceReadyPickup,
        })
        await mutateData()
        closeDetail()
        // refresh detail biar status update (opsional)
        try {
        const res = await apiGetOrderDetail(detail.id)
        setDetail(res)
        } catch {}
    } catch (e) {
        console.error(e)
        alert('Gagal set ready to pickup.')
    } finally {
        setActionLoading(false)
    }
    }, [detail, mutateData, forceReadyPickup])

    const apiProceed = async ({ orderId, note, force }) => {
    // sesuaikan endpoint Yoiboi kamu
    return axios.post(
        `${process.env.REACT_APP_YOIBOI_API_URL}/api/v1/orders/${orderId}/proceed`,
        { note, force }
    )
    }

    const onProceed = useCallback(async () => {
    if (!detail?.id) return
    const ok = window.confirm('Proceed order ini?')
    if (!ok) return

    setActionLoading(true)
    try {
        await apiProceed({
        orderId: detail.id,
        note: 'Proceed dari CMS',
        force: !!forceReadyPickup, // kalau kamu mau reuse checkbox yang sama
        })

        await mutateData()
        closeDetail()

        // refresh detail biar status berubah di modal
        try {
        const res = await apiGetOrderDetail(detail.id)
        setDetail(res)
        } catch {}

        // opsional: kalau mau auto-close setelah proceed
        // closeDetail()
    } catch (e) {
        console.error(e)
        alert('Gagal proceed order.')
    } finally {
        setActionLoading(false)
    }
    }, [detail, mutateData, forceReadyPickup])

    const apiFinish = async ({ orderId }) => {
    return axios.post(
        `${process.env.REACT_APP_YOIBOI_API_URL}/api/v1/orders/${orderId}/finish`
    )
    }

    const onFinish = useCallback(async () => {
    if (!detail?.id) return
    const ok = window.confirm('Set order ini sebagai DONE?')
    if (!ok) return

    setActionLoading(true)
    try {
        await apiFinish({ orderId: detail.id })

        await mutateData()
        closeDetail()

        // kalau kamu mau refresh detail instead of close:
        // const res = await apiGetOrderDetail(detail.id); setDetail(res)
    } catch (e) {
        console.error(e)
        alert('Gagal set as done.')
    } finally {
        setActionLoading(false)
    }
    }, [detail, mutateData, closeDetail])



    useEffect(() => {
        window.history.replaceState({}, document.title)

        if (transition.reverse) {
            setTransition({ type: 'slide', reverse: false })
        }
    }, [])

    useEffect(() => {
        searchInput.current.focus()
    }, [search])

    // Watch changes on petty cash data and loading state
    useEffect(() => {
        if (search === "") {
            navigate(``, { replace: true })
        }

        if (isLoadingdata) {
            nProgress.start()
        } else {
            nProgress.done()
        }
    }, [data, isLoadingdata])

    // Data filter handlers
    const updateFilter = (value) => {
        setSearchParams(value)
    }

    const removeFilter = () => {
        setSearchParams({})
    }

    // Data sort handlers
    const toggleSort = (value) => {
        if ((direction === null || direction === 'desc') || orderBy !== value) {
            setSearchParams({
                order_by: value,
                direction: 'asc'
            })
        } else {
            setSearchParams({
                order_by: value,
                direction: 'desc'
            })
        }
    }

    function prettyStatus(status) {
        const up = String(status ?? '').toUpperCase();
        if (up === 'PENDING_CONFIRMATION') return 'Waiting for Confirmation';
        if (up === 'READY_TO_PICKUP') return 'Ready To Pickup';
        // fallback: humanize other statuses
        return String(status ?? '')
        .replace(/_/g, ' ')
        .replace(/\w\S*/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase());
    }

    function prettyFallbackPaymentMethod(method){
        const up = String(method ?? '').toUpperCase();
        if (up === 'voucher_and_coupon') return 'Voucher and Coupon';
        if (up === 'points_and_vouchers') return 'Points and Voucher';
        if (up === 'points') return 'Points';
        // fallback: humanize other statuses
        return String(method ?? '')
            .replace(/_/g, ' ')
            .replace(/\w\S*/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase());
    }

    function statusColor(status) {
        const s = String(status || '').toUpperCase();
        switch (s) {
            case 'PAID':
            return 'bg-green-100 text-green-700';
            case 'PENDING':
            case 'PENDING_CONFIRMATION':
            return 'bg-yellow-100 text-yellow-700';
            case 'READY_TO_PICKUP':
            return 'bg-emerald-100 text-emerald-700';
            case 'FAILED':
            return 'bg-red-100 text-red-700';
            default:
            return 'bg-gray-100 text-gray-600';
        }
    }

    const currency = (value)=>{
        return Intl.NumberFormat('id-Id', { style: 'currency', currency: 'idr' }).format(value)
    }

    const branchNameById = useMemo(() => {
    const list = branchData?.data ?? branchData ?? []; // jaga-jaga kalau shape-nya beda
    const map = new Map();
    for (const b of list) {
        map.set(String(b.id), b.name);
    }
    return map;
    }, [branchData]);

    const getOutletName = (outletId) => {
    if (outletId == null) return "-";
    return branchNameById.get(String(outletId)) ?? "-";
    };


    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between text-xs">
                <div className="flex items-center sm:space-x-2">
                    <div className="relative hidden sm:block">
                        <div className="absolute inset-y-0 flex items-center pl-3 pointer-events-none">
                            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" width={24} height={24} viewBox="0 0 24 24" strokeWidth={1} stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                                <circle cx={10} cy={10} r={7}></circle>
                                <line x1={21} y1={21} x2={15} y2={15}></line>
                            </svg>
                        </div>
                        <input ref={searchInput} onChange={(e) => setSearchTemp(e.target.value)} value={searchTemp} type="text" placeholder={`${lang.search} Orders`} autoComplete="off" className="w-64 py-3 pl-8 pr-4 text-xs transition border border-neutral-200 focus:outline-none rounded-xl focus:border-neutral-400 focus:ring focus:ring-neutral-200" />
                    </div>
                    <Filter onSubmit={updateFilter} onRemove={removeFilter} data={Object.fromEntries(Object.entries({ productUnit, branch, from, to,status }).filter(([_, v]) => v != null))} />
                </div>
            </div>
            <div className="overflow-x-auto border border-neutral-200 rounded-xl">
                <table className="min-w-full overflow-x-auto divide-y divide-neutral-200">
                    <thead className="bg-neutral-50 rounded-t-3xl">
                        <tr>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Order Id</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Outlet</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Customer</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Fullfillment Type</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Payment Method</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Total</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">Status</th>
                            <th scope="col" className="px-6 py-3 text-xs font-medium text-left uppercase text-neutral-500 whitespace-nowrap">{lang.created_at}</th>
                            <th scope="col" className="relative px-6 py-3"><span className="sr-only">{lang.action}</span></th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-neutral-200">
                        {/* When loading */}
                        {isLoadingdata && (
                            <tr className="text-center">
                                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.loading_data}
                                </td>
                            </tr>
                        )}

                        {/* When there are no list available */}
                        {data?.data.length === 0 && !search && !isLoadingdata && (
                            <tr className="text-center">
                                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.no_data}
                                </td>
                            </tr>
                        )}

                        {/* When there are no list available on searching */}
                        {data?.data.length === 0 && search && !isLoadingdata && (
                            <tr className="text-center">
                                <td colSpan="10" className="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {lang.no_result}
                                </td>
                            </tr>
                        )}

                        {data?.data.length > 0 && data.data.map((row) => (
                            <tr key={row.id} onClick={() => openDetail(row.id)}>
                                <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    <div className="flex flex-col space-y-1">
                                        <div>{row.id}</div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                {isLoadingBranchSelection ? "Loading..." : getOutletName(row.outlet_id)}
                                </td>
                                <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                    {row.customer}
                                </td>
                                <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                    {row.fulfillment_type}
                                </td>
                                <td className="px-4 py-3">{row.payments?.[0]?.payment_method?.name ?? prettyFallbackPaymentMethod(row.payments?.[0]?.payment_type) ?? '-'}</td>
                                <td className="px-6 py-4 text-xs font-medium text-neutral-900 whitespace-nowrap">
                                    {currency(row.nett_amount)}
                                </td>
                                <td className="px-4 py-3">
                                    <span
                                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor(
                                        row.payment_status || ''
                                    )}`}
                                    >
                                    {prettyStatus(row.payment_status)}
                                    </span>
                                </td>
                                <td class="px-6 py-4 text-xs text-neutral-500 whitespace-nowrap">
                                    {moment(row.created_at).format('MMMM D, YYYY')}
                                </td>
                                <td className="px-6 py-4 text-xs font-medium text-right whitespace-nowrap">
                                {/* <button
                                    type="button"
                                    onClick={() => openDetail(row.id)}
                                    className="px-3 py-1.5 rounded-lg border border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50"
                                >
                                    Detail
                                </button> */}
                                </td>

                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <Pagination links={data?.links} from={data?.from} to={data?.to} total={data?.total} />
            {isDetailOpen && (
                <div className="fixed inset-0 z-50 bg-black/40">
                    {/* overlay */}
                    <div
                        className="absolute inset-0"
                        onClick={closeDetail}
                    />
                    {/* modal */}
                    <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="w-full max-w-3xl rounded-2xl bg-white shadow-xl overflow-hidden">
                        {/* header */}
                        <div className="flex items-start justify-between px-5 py-4 border-b">
                        <div className="space-y-1">
                            <div className="text-sm font-semibold text-neutral-900">
                            Order Detail {detail?.id ? `#${detail.id}` : ''}
                            </div>
                            <div className="text-xs text-neutral-500">
                            {detail?.order_date ? moment(detail.order_date).format('MMMM D, YYYY HH:mm') : '-'}
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={closeDetail}
                            className="p-2 rounded-lg hover:bg-neutral-100"
                            aria-label="Close"
                        >
                            ✕
                        </button>
                        </div>

                        {/* body */}
                        <div className="p-5 max-h-[70vh] overflow-y-auto">
                        {loadingDetail && (
                            <div className="text-xs text-neutral-500">Loading detail...</div>
                        )}

                        {!loadingDetail && !detail && (
                            <div className="text-xs text-neutral-500">Detail tidak tersedia.</div>
                        )}

                        {!loadingDetail && detail && (
                            <div className="space-y-5">
                            {/* status + summary */}
                            <div className="flex flex-wrap items-center gap-2">
                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${statusColor(detail.payment_status)}`}>
                                {prettyStatus(detail.payment_status)}
                                </span>

                                <span className="text-xs text-neutral-500">
                                Fulfillment: <span className="text-neutral-900 font-medium">{detail.fulfillment_type ?? '-'}</span>
                                </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                                <div className="rounded-xl border p-3">
                                <div className="text-neutral-500">Outlet</div>
                                <div className="font-medium text-neutral-900">
                                    {detail?.pickup_location?.name ?? (detail?.outlet_id ? getOutletName(detail.outlet_id) : '-')}
                                </div>
                                <div className="text-neutral-500 mt-1">
                                    {detail?.pickup_location?.address ?? '-'}
                                </div>
                                </div>

                                <div className="rounded-xl border p-3">
                                <div className="text-neutral-500">Payment</div>
                                <div className="font-medium text-neutral-900">
                                    {detail?.payments?.[0]?.name ?? '-'}
                                </div>
                                <div className="text-neutral-500 mt-1">
                                    Status: {String(detail?.payments?.[0]?.payment_status ?? '-')}
                                </div>
                                </div>
                            </div>

                            {/* amounts */}
                            <div className="rounded-xl border p-3 text-xs">
                                <div className="font-semibold text-neutral-900 mb-2">Ringkasan</div>
                                <div className="grid grid-cols-2 gap-y-2">

                                <div className="text-neutral-500">Point Reward</div>
                                <div className="text-right font-medium">{formatUang(detail.reward_amount)}</div>

                                <div className="text-neutral-500">Redeem Points</div>
                                <div className="text-right font-medium">{formatUang(detail.redeem_points)}</div>
                                
                                <div className="text-neutral-500">Amount</div>
                                <div className="text-right font-medium">{money(detail.amount)}</div>

                                <div className="text-neutral-500">Discount</div>
                                <div className="text-right font-medium">- {money(detail.discount_amount)}</div>

                                <div className="text-neutral-500">PPN</div>
                                <div className="text-right font-medium">{money(detail.ppn_amount)}</div>

                                <div className="text-neutral-500">Shipping</div>
                                <div className="text-right font-medium">{money(detail.shipping_amount)}</div>

                                <div className="text-neutral-900 font-semibold">Nett Total</div>
                                <div className="text-right text-neutral-900 font-semibold">{money(detail.nett_amount)}</div>
                                </div>
                            </div>

                            {/* items */}
                            <div className="rounded-xl border overflow-hidden">
                                <div className="px-3 py-2 bg-neutral-50 text-xs font-semibold text-neutral-900">
                                Items ({detail.items?.length ?? 0})
                                </div>
                                <div className="divide-y">
                                {(detail.items ?? []).map((it) => {
                                    const visibleAttrs = (it.attributes ?? []).filter(a => String(a.is_visible) === '1')
                                    return (
                                    <div key={it.item_number} className="p-3 text-xs">
                                        <div className="flex items-start justify-between gap-3">
                                        <div>
                                            <div className="font-medium text-neutral-900">{it.product_unit}</div>
                                            <div className="text-neutral-500">Qty: {it.quantity}</div>
                                        </div>
                                        <div className="text-right font-medium text-neutral-900">
                                            {money(it.total_nett)}
                                        </div>
                                        </div>

                                        {visibleAttrs.length > 0 && (
                                        <div className="mt-2 flex flex-wrap gap-1">
                                            {visibleAttrs.map((a) => (
                                            <span
                                                key={`${a.attribute_group_id}-${a.attribute_id}`}
                                                className="inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] text-neutral-700"
                                            >
                                                {a.attribute_group}: {a.attribute}
                                            </span>
                                            ))}
                                        </div>
                                        )}
                                    </div>
                                    )
                                })}
                                </div>
                            </div>
                            </div>
                        )}
                        </div>

                        {/* footer actions */}
                        <div className="px-5 py-4 border-t flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 text-xs">
                            {/* PROCEED => ready pickup */}
                            {String(detail?.payment_status ?? '').toUpperCase() === 'PROCEED' && (
                            <label className="inline-flex items-center gap-2 text-neutral-600">
                                <input
                                type="checkbox"
                                checked={forceReadyPickup}
                                onChange={(e) => setForceReadyPickup(e.target.checked)}
                                />
                                Force
                            </label>
                            )}
                        </div>

                        <div className="flex items-center gap-2">
                            <button
                            type="button"
                            onClick={closeDetail}
                            className="px-4 py-2 text-xs rounded-xl border border-neutral-200 hover:bg-neutral-50"
                            >
                            Close
                            </button>

                            {String(detail?.payment_status ?? '').toUpperCase() === 'PAID' && (
                            <button
                                type="button"
                                disabled={actionLoading}
                                onClick={onVoid}
                                className="px-4 py-2 text-xs rounded-xl bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-60"
                            >
                                {actionLoading ? 'Processing...' : 'Void'}
                            </button>
                            )}
                            {/* PENDING_CONFIRMATION => reject */}
                            {String(detail?.payment_status ?? '').toUpperCase() === 'PENDING_CONFIRMATION' && (
                            <button
                                type="button"
                                disabled={actionLoading}
                                onClick={onCancel}
                                className="px-4 py-2 text-xs rounded-xl bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                            >
                                {actionLoading ? 'Processing...' : 'Cancel'}
                            </button>
                            )}
                            {String(detail?.payment_status ?? '').toUpperCase() === 'PENDING_CONFIRMATION' && (
                            <button
                                type="button"
                                disabled={actionLoading}
                                onClick={onApprove}
                                className="px-4 py-2 text-xs rounded-xl bg-green-600 text-white hover:bg-green-700 disabled:opacity-60"
                            >
                                {actionLoading ? 'Processing...' : 'Approve'}
                            </button>
                            )}
                            {String(detail?.payment_status ?? '').toUpperCase() === 'READY_TO_PICKUP' && (
                            <button
                                type="button"
                                disabled={actionLoading}
                                onClick={onFinish}
                                className="px-4 py-2 text-xs rounded-xl bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                            >
                                {actionLoading ? 'Processing...' : 'Set as Done'}
                            </button>
                            )}


                            {String(detail?.payment_status ?? '').toUpperCase() === 'PAID' && (
                            <button
                                type="button"
                                disabled={actionLoading}
                                onClick={onProceed}
                                className="px-4 py-2 text-xs rounded-xl bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-60"
                            >
                                {actionLoading ? 'Processing...' : 'Proceed'}
                            </button>
                            )}


                            {/* PROCEED => ready pickup */}
                            {String(detail?.payment_status ?? '').toUpperCase() === 'PROCEED' && (
                            <button
                                type="button"
                                disabled={actionLoading}
                                onClick={onReadyPickup}
                                className="px-4 py-2 text-xs rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60"
                            >
                                {actionLoading ? 'Processing...' : 'Set Ready To Pickup'}
                            </button>
                            )}
                        </div>
                        </div>
                    </div>
                    </div>
                </div>
                )}

        </div>
    )
}
